<template>
  <div v-if="showInstallPrompt" class="pwa-install-banner">
    <div class="install-content">
      <div class="install-icon">📱</div>
      <div class="install-text">
        <h3>Install AI Bella</h3>
        <p>Get the full app experience on your device!</p>
      </div>
      <div class="install-actions">
        <button @click="installApp" class="install-btn">Install</button>
        <button @click="dismissPrompt" class="dismiss-btn">×</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PwaInstallPrompt',
  data() {
    return {
      showInstallPrompt: false,
      deferredPrompt: null
    }
  },
  mounted() {
    this.initInstallPrompt();
    this.checkIfAlreadyPrompted();
  },
  methods: {
    initInstallPrompt() {
      // Listen for the beforeinstallprompt event
      window.addEventListener('beforeinstallprompt', (e) => {
        // Prevent the mini-infobar from appearing on mobile
        e.preventDefault();
        // Save the event so it can be triggered later
        this.deferredPrompt = e;
        // Show the install prompt
        this.showInstallPrompt = true;
      });

      // Handle the app installed event
      window.addEventListener('appinstalled', () => {
        console.log('AI Bella PWA was installed');
        this.showInstallPrompt = false;
        this.deferredPrompt = null;
        localStorage.setItem('pwa-installed', 'true');
      });
    },
    
    checkIfAlreadyPrompted() {
      // Don't show if already installed or dismissed recently
      const isInstalled = localStorage.getItem('pwa-installed') === 'true';
      const isDismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedTime = isDismissed ? parseInt(isDismissed) : 0;
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      
      if (isInstalled || (isDismissed && dismissedTime > oneDayAgo)) {
        this.showInstallPrompt = false;
      }
    },
    
    async installApp() {
      if (!this.deferredPrompt) {
        return;
      }
      
      // Show the install prompt
      this.deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      // Clear the deferredPrompt
      this.deferredPrompt = null;
      this.showInstallPrompt = false;
    },
    
    dismissPrompt() {
      this.showInstallPrompt = false;
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }
  }
}
</script>

<style scoped>
.pwa-install-banner {
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  z-index: 1000;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  color: white;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.install-content {
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 12px;
}

.install-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.install-text {
  flex: 1;
  min-width: 0;
}

.install-text h3 {
  margin: 0 0 4px 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.install-text p {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
}

.install-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.install-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.install-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.dismiss-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  padding: 4px;
  line-height: 1;
}

.dismiss-btn:hover {
  opacity: 1;
}

/* Mobile responsiveness */
@media (max-width: 480px) {
  .pwa-install-banner {
    left: 10px;
    right: 10px;
    bottom: 10px;
  }
  
  .install-content {
    padding: 12px;
    gap: 10px;
  }
  
  .install-text h3 {
    font-size: 1rem;
  }
  
  .install-text p {
    font-size: 0.85rem;
  }
  
  .install-btn {
    padding: 6px 12px;
    font-size: 0.85rem;
  }
}

/* iOS specific styles */
@supports (-webkit-touch-callout: none) {
  .pwa-install-banner {
    bottom: env(safe-area-inset-bottom, 20px);
  }
}
</style> 